import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FlexDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { CachedRepositoryService, CacheHelperService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { BaseGridViewMetadata, ClassMetadata, GridMetadata, IGridViewMetadata } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, EntityWrapper, LazyLoadedProxyHandler, ModifierCommandArgs, RepositoryFormData, RowCommandArgs, RowCommandRow, TableRowSelectArgs, } from "../../../interfaces/";
import { GridEventService, GridFetchType, GridLoadService, GridSelectionService, InfoGridLayoutService, MetadataActionService, MetadataEventService, MetadataPropertyService, MetadataService, MetadataViewService, ProviderStorageService, } from "../../../services";
import { ObjectHelpers } from "../../../utilities";
import { MetadataPanelComponent } from "../../metadataPanel/metadataPanel.component";
import { InfoGridComponent } from "../infoGrid/infoGrid.component";

export interface RepositoryFunctionArgs
{
    entity: AugmentedModelObject;
    command: string;
}
@Component({
    selector: "metadata-table",
    templateUrl: "./metadata.table.tmpl.html",
    imports: [
        FlexDirective,
        LayoutDirective,
        FormsModule,
        InfoGridComponent,
        MetadataPanelComponent,
        ReactiveFormsModule,
    ]
})
export class MetadataTableComponent implements OnInit
{
    private readonly $cacheHelperService = inject(CacheHelperService);
    private $repositoryService = inject(CachedRepositoryService);
    private readonly $metadataService = inject(MetadataService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private $providerStorageService = inject(ProviderStorageService);
    private $metadataEventService = inject(MetadataEventService);
    private $gridEventService = inject(GridEventService);
    private $gridLayoutService = inject(InfoGridLayoutService);
    private $gridLoadService = inject(GridLoadService);
    private $metadataViewService = inject(MetadataViewService);
    private $gridSelectionService = inject(GridSelectionService);

    _entities: any[] = [];

    _gridMetadata: GridMetadata;

    metadata: ClassMetadata;

    @Input() tableTitle: string;

    @Input() displayHeader: boolean;

    @Input() disableViewAction: boolean = false;

    @Input() repositoryFunction: (args: RepositoryFunctionArgs) => Promise<any>;

    @Input() preRepositoryFunction: (args: RepositoryFunctionArgs) => boolean;

    @Input() serverPaging: boolean;

    @Input() viewMetadata: IGridViewMetadata = {} as IGridViewMetadata;

    _filterDataController: RepositoryFormData;

    @Input() datacontroller: RepositoryFormData;

    @Output() modifierCallback = new EventEmitter<ModifierCommandArgs>();

    @Input() parentType: string;

    @Input() isStepperGrid: boolean;

    loading: boolean = true;

    @Input() model: any;

    @Input() multipleEdit: boolean;

    @Input() parentView: string;

    @Input() disableGridAdd: boolean = false;

    recalculatingGridView: boolean = false;

    constructor()
    {
        const $providerStorageService = this.$providerStorageService;
        const $gridEventService = this.$gridEventService;
        const $gridLayoutService = this.$gridLayoutService;
        const $metadataViewService = this.$metadataViewService;

        const self = this;
        this.$gridEventService.onLoadMetadataTable.pipe(takeUntilDestroyed())
            .subscribe(arg =>
            {
                let doLoad: boolean = true;
                self.$providerStorageService.ReloadColumns[self.gridMetadata["$providerId"]] = arg.ReloadColumns;
                self.$providerStorageService.ClearFilter[self.gridMetadata["$providerId"]] = arg.ClearFilter;
                self.$providerStorageService.ApplyFilter[self.gridMetadata["$providerId"]] = arg.ApplyFilter;
                if (
                    !Utilities.IsNullOrUndefinedOrEmpty(self.viewMetadata.SubPropertyName) &&
                    !Utilities.IsNullOrUndefinedOrEmpty(arg.PropertiesToRecalc)
                )
                {
                    if (arg.PropertiesToRecalc[0] === "$ViewMetadata")
                        delete self.$gridLayoutService.LocalCachedLayout[self.gridMetadata["$providerId"]];
                    else if (arg.PropertiesToRecalc[0] !== "$All")
                    {
                        const subPropertyChain = self.viewMetadata.SubPropertyName.split(".");
                        const subProperty = subPropertyChain[subPropertyChain.length - 1];
                        const propertiesToRecalc = arg.PropertiesToRecalc.map(p =>
                        {
                            const splitProperty = p.split(".");
                            return splitProperty[splitProperty.length - 1];
                        });
                        if (propertiesToRecalc.indexOf(subProperty) === -1)
                            //subproperty is not explicitly being recalculated, check properties
                            doLoad = self.shouldLoad(propertiesToRecalc);
                    }
                }
                if ((arg.Type === self.gridMetadata.ClassMetadata.TypeName || arg.AllSubPropertyGrids) && doLoad)
                {
                    if (arg.Refetch === true && self.$metadataViewService.IsProjectionGrid(self.viewMetadata) && !this.viewMetadata.SubPropertyName)
                    {
                        const providerId = self.gridMetadata["$providerId"];
                        self.$providerStorageService.Refetch[providerId] = true;
                        if (self.viewMetadata["$$OriginalConstantFilter"] !== undefined)
                            self.viewMetadata.ConstantFilter = self.$metadataViewService.createFilterString(self.datacontroller.Entity, self.viewMetadata)?.replace("&$filter=", "");
                        self.$gridEventService.fireTableEntitiesChangedListeners(providerId);
                    }
                    else
                    {
                        self.initializeTable(true);
                    }
                }
            });
    }

    shouldLoad(propertiesToRecalc: string[]): boolean
    {
        let doLoad = false;
        const propsToCheck: string[] = [];
        pushProp(this.viewMetadata.CanAddProperty);
        pushProp(this.viewMetadata.CanEditProperty);
        pushProp(this.viewMetadata.CanDeleteProperty);
        pushProp(this.viewMetadata.TitleProperty);
        pushProp(this.viewMetadata.SelectProperty ? this.viewMetadata.SelectProperty : "Selected");
        pushProp(this.viewMetadata.GridProperties.CanEditProperty);
        pushProp(this.viewMetadata.GridProperties.CanDeleteProperty);
        pushProp(this.viewMetadata.GridProperties.CanCopyProperty);
        this.viewMetadata.GridProperties.ClassMetadata.Properties.forEach(p =>
        {
            pushProp(p.HiddenProperty);
            pushProp(p.DisabledProperty);
            if (p.ActionMetadata)
            {
                pushProp(p.ActionMetadata.HiddenProperty);
                pushProp(p.ActionMetadata.DisabledProperty);
            }
        });
        this.viewMetadata.GridProperties.ClassMetadata.Properties.forEach(p => pushProp(p.Name));
        propertiesToRecalc.forEach(p =>
        {
            if (!doLoad && propsToCheck.indexOf(p) > -1) doLoad = true;
        });
        function pushProp(property: string): void
        {
            if (property)
            {
                const splitProperty = property.split(".");
                property = splitProperty[splitProperty.length - 1];
                if (property.startsWith("!")) property = property.substring(1);
                if (propsToCheck.indexOf(property) === -1) propsToCheck.push(property);
            }
        }
        return doLoad;
    }

    ngOnInit(): void
    {
        this.multipleEdit = this.multipleEdit && !this.serverPaging;
        this.initializeTable(true);
    }

    get UseRepository(): boolean
    {
        return !Utilities.IsNullOrUndefined(this.repositoryFunction);
    }

    doRepositoryFunction($event: RowCommandArgs): void
    {
        const self = this;
        const command: string = $event.command;
        const row: RowCommandRow = $event.row;
        if (!Utilities.IsNullOrUndefined(row))
        {
            if (
                this.preRepositoryFunction &&
                this.preRepositoryFunction(<RepositoryFunctionArgs>{
                    entity: row.model,
                    command: command,
                })
            )
            {
                // if this command is handled, end execution now
                return;
            }
            if (this.$metadataViewService.IsProjectionGrid(this.viewMetadata))
            {
                if (command === "Copy" || command === "Edit")
                    doFunction(row.model);
                else
                {
                    const rowTypeName = this.viewMetadata.GridProperties["RowTypeName"];
                    const idProperty = this.viewMetadata.GridProperties["IdProperty"];
                    const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
                        this.datacontroller,
                        () => self.datacontroller.findExistingOrGet(rowTypeName, idProperty, row.id, self.$repositoryService, this.datacontroller.ConfirmationQuestions)
                            .then(result =>
                            {
                                if (!result["$isEntityWrapper"])
                                {
                                    const modelWrapper = new EntityWrapper(result, this.$metadataService);
                                    result = new Proxy(modelWrapper, new LazyLoadedProxyHandler(this.$metadataService));
                                }
                                result.$$TypeName = rowTypeName;
                                if (!self.datacontroller.Entity)
                                {
                                    self.datacontroller.Entity = result;
                                    self.datacontroller.Type = rowTypeName;
                                }
                                else self.datacontroller.Add(rowTypeName, result);
                                doFunction(result);
                            }),
                        null
                    );

                    if (command === "Delete")
                    {
                        self.datacontroller.Clear(rowTypeName);
                        self.$cacheHelperService.clearAllForType(rowTypeName);
                    }
                    self.datacontroller.findExistingOrGet(rowTypeName, idProperty, row.id, self.$repositoryService, this.datacontroller.ConfirmationQuestions)
                        .then(result =>
                        {
                            result.$$TypeName = rowTypeName;
                            if (!self.datacontroller.Entity)
                            {
                                self.datacontroller.Entity = result;
                                self.datacontroller.Type = rowTypeName;
                            }
                            else self.datacontroller.Add(rowTypeName, result);
                            doFunction(result);
                        }, rejectionHandler);
                }
            }
            else if (this.serverPaging)
            {
                self.$repositoryService
                    .Get({
                        Item1: row.model.$$TypeName,
                        Item2: row.id,
                    })
                    .then(result =>
                    {
                        doFunction(result);
                    });
            }
            else
            {
                doFunction(row.model);
            }
        }
        else if (command === "Add" || command === "EditMultiple")
        {
            doFunction(null);
        }
        function doFunction(entity: any): void
        {
            self.repositoryFunction(<RepositoryFunctionArgs>{
                entity: entity,
                command: command,
            })
                .then(() =>
                {
                    if ((command === "Delete" || command === "Edit") && !self.$metadataViewService.IsProjectionGrid(self.viewMetadata))
                    {
                        self.$providerStorageService.SkipLayout[self.gridMetadata["$providerId"]] = true;
                        self.initializeTable(true);
                    }
                })
                .finally(() =>
                {
                    if (self.viewMetadata && self.viewMetadata["$$Deleting"])
                    {
                        self.viewMetadata["$$Deleting"] = false;
                        self.$gridSelectionService.SetSelectedIds(self.gridMetadata["$providerId"], []);
                    }
                });
        }
    }

    @Input()
    get gridMetadata(): GridMetadata
    {
        return this._gridMetadata;
    }

    set gridMetadata(v: GridMetadata)
    {
        this._gridMetadata = v;
        this.metadata = Utilities.IsNullOrUndefined(v) ? null : v.ClassMetadata;
    }

    @Input()
    get entities(): any[]
    {
        return this._entities;
    }

    set entities(value: any[])
    {
        const isUpdate = this._entities !== value;
        this._entities = value;
        this.initializeTable(isUpdate);
    }

    onSelected($event: TableRowSelectArgs): void
    {
        const self = this;
        const selectAll: boolean = $event.selectAll;
        const deselectAll: boolean = $event.deselectAll;
        const added: string[] = $event.added;
        const removed: string[] = $event.removed;
        const prop = this.viewMetadata.SelectProperty ? this.viewMetadata.SelectProperty : "Selected";
        const selectProperty = this.metadata.Properties.filter(p => p.Name === prop)[0];
        const recalculates = [];
        if (!selectAll && !deselectAll)
        {
            if (removed.length > 0)
            {
                removed.forEach(id =>
                {
                    recalculates.push({
                        Id: id,
                        Added: false,
                    });
                });
            }
            if (added.length > 0)
            {
                added.forEach(id =>
                {
                    recalculates.push({
                        Id: id,
                        Added: true,
                    });
                });
            }
            if (selectProperty?.RecalculateWithParent ?? false)
            {
                recalculateSerial();
            }
            else
            {
                recalculates.forEach(recalc =>
                {
                    recalculate(recalc.Id, recalc.Added);
                });
            }
            return;
        }
        if (deselectAll)
        {
            this._entities
                .filter(x => x["$inFilter"])
                .forEach(entity =>
                {
                    if (
                        Utilities.IsNullOrUndefinedOrEmpty(this.viewMetadata.GridProperties.AllowSelectProperty) ||
                        this.$metadataService.EvaluateBoolean(
                            this.viewMetadata.GridProperties.AllowSelectProperty,
                            entity["$$Parent"],
                            entity
                        )
                    )
                        entity[prop] = false;
                });
        }
        if (selectAll)
        {
            this._entities
                .filter(x => x["$inFilter"])
                .forEach(entity =>
                {
                    if (
                        Utilities.IsNullOrUndefinedOrEmpty(this.viewMetadata.GridProperties.AllowSelectProperty) ||
                        this.$metadataService.EvaluateBoolean(
                            this.viewMetadata.GridProperties.AllowSelectProperty,
                            entity["$$Parent"],
                            entity
                        )
                    )
                        entity[prop] = true;
                });
        }
        if (selectProperty && selectProperty.RecalculateOnChange)
        {
            this.$metadataEventService.fireRecalculateWorkingListeners(
                {
                    ShowLoading: false,
                    Recalculating: true,
                }, false);
            self.recalculatingGridView = true;
            this.$repositoryService
                .RecalculateGridView({
                    Item1: this.datacontroller,
                    Item2: self.viewMetadata["GridId"],
                })
                .then(
                    rdc =>
                    {
                        self.datacontroller.Merge(rdc);
                        if (selectProperty?.RecalculateWithParent && selectProperty.RecalculateOnChange)
                        {
                            const parent = self._entities?.[0]?.$$Parent;
                            if (parent)
                            {
                                for (const prop of selectProperty.RecalculateOnChange)
                                {
                                    if (prop.startsWith("Parent."))
                                    {
                                        const parentPropName = prop.substring("Parent.".length);
                                        self.$metadataService.fireProxyPropertyChangedListeners({
                                            Model: parent,
                                            Property: parentPropName,
                                            OldValue: undefined,
                                            NewValue: parent[parentPropName]
                                        });
                                    }
                                }
                            }
                        }
                        self.$metadataService.FireGridReload(rdc.Type, true);
                        self.$metadataEventService.fireRecalculateWorkingListeners(
                            {
                                ShowLoading: false,
                                Recalculating: false,
                            }, false);
                    },
                    self.$metadataActionService.GetVerificationHandler(self.datacontroller, () =>
                        Promise.resolve(
                            self.onSelected(<TableRowSelectArgs>{
                                selectAll: $event.selectAll,
                                deselectAll: $event.deselectAll,
                                added: $event.added,
                                removed: $event.removed,
                            })
                        )
                    )
                )
                .finally(() =>
                {
                    self.recalculatingGridView = false;
                });
        }
        else
        {
            self.$gridEventService.fireUpdateGridSelectionListeners(
                {
                    ProviderId: self.gridMetadata["$providerId"],
                    Clear: false
                }
            );
        }
        function recalculate(id: string, added: boolean): Promise<any>
        {
            if (!self._entities)
                return Promise.resolve();
            const model = self._entities.filter(
                e => ObjectHelpers.GetId(self.gridMetadata.IdProperty, e, 0) === id
            )[0];
            model[prop] = added;
            if (selectProperty && selectProperty.RecalculateOnChange)
            {
                return self.$metadataPropertyService.recalculateProperties(
                    self.metadata.TypeName,
                    selectProperty,
                    model,
                    model.$$Parent,
                    added,
                    undefined,
                    false,
                    false
                );
            }
            return Promise.resolve();
        }
        function recalculateSerial(): Promise<any>
        {
            if (Utilities.IsNullOrUndefinedOrEmpty(recalculates) || self.recalculatingGridView) return Promise.resolve();
            const thing = recalculates.shift();
            return recalculate(thing.Id, thing.Added)
                .then(recalculateSerial);
        }
    }

    private initializeTable(isUpdate: boolean): Promise<any>
    {
        if (!isUpdate || Utilities.IsNullOrUndefined(this.metadata))
            return;
        if (Utilities.IsNullOrUndefined(this.gridMetadata["$providerId"]))
            this.gridMetadata["$providerId"] = "metadataTable_" + Utilities.generateRandomGuid();
        this.viewMetadata.GridProperties = this.gridMetadata;
        this.$gridLoadService.setGridFetchType(this.viewMetadata, this.gridMetadata["$providerId"]);
        this.viewMetadata.Title = this.tableTitle;

        if (this.$providerStorageService.GridFetchType[this.gridMetadata["$providerId"]] === GridFetchType.ServerStore)
        {
            this.loading = false;
            return;
        }

        const self = this;
        this.$gridLoadService
            .augmentAndPopulateLocalStore(
                self.gridMetadata["$providerId"],
                self.metadata,
                self._entities,
                self.viewMetadata
            )
            .then(() =>
            {
                self.$gridEventService.fireTableEntitiesChangedListeners(self.gridMetadata["$providerId"]);
                self.loading = false;

                // This will need to be revisited with the Angular 22 update
                setTimeout(() =>
                {
                    const headerWrapper = document.querySelectorAll(".igx-grid-thead__wrapper");
                    headerWrapper.forEach((header: Element) => {
                        header.removeAttribute("role");
                        header.parentElement?.setAttribute("role", "rowgroup");
                    });

                    const selectAllCheckboxes = document.querySelectorAll(".igx-grid-thead__wrapper .igx-grid__cbx-selection.igx-grid__tr-action");
                    selectAllCheckboxes.forEach((checkbox: Element) => {
                        checkbox.setAttribute("role", "gridcell");
                    });

                    const bodyContent = document.querySelectorAll("div.igx-grid__tbody");
                    bodyContent.forEach((body: Element) => {
                        body.removeAttribute("role");
                        body.children[0]?.setAttribute("role", "rowgroup");
                    });
                }, 0);
            });
    }

    get hasFilter(): boolean
    {
        return (
            !Utilities.IsNullOrUndefined(this.gridMetadata) &&
            !Utilities.IsNullOrUndefined(this.gridMetadata.FilterMetadata) &&
            !Utilities.IsNullOrUndefined(this.modifierCallback)
        );
    }

    get filterModel(): any
    {
        if (Utilities.IsNullOrUndefined(this._gridMetadata["$$filterModel"]))
        {
            const model = {};
            model["$$Parent"] = this.datacontroller.Entity;
            this._gridMetadata["$$filterModel"] = model;
        }
        return this._gridMetadata["$$filterModel"];
    }

    get filterDataController(): any
    {
        if (Utilities.IsNullOrUndefined(this._filterDataController))
        {
            this._filterDataController = new RepositoryFormData(
                this.gridMetadata.FilterMetadata.TypeName,
                this.filterModel
            );
        }
        return this._filterDataController;
    }

    filterCallback(arg: ModifierCommandArgs): void
    {
        if (Utilities.IsNullOrUndefined(this.modifierCallback))
        {
            throw new Error(`modifierCallback not defined.`);
        }
        this.modifierCallback.emit(<ModifierCommandArgs>{
            model: arg.model,
            propertyChanged: "FilterParameterChanged",
            propertyValue: null,
        });
    }
}
